import { FC } from "react";
import { useTranslation } from "react-i18next";
import Text from "@/components/atoms/Text";
import Spinner from "@/components/atoms/Spinner/Spinner";
import { classNames } from "@/utils/Classnames";
import { CollectionSlot } from "@/types";
import {
  formatSlotRange,
  formatSlotTime,
  isSlotPast,
  isSlotSelectable,
  sortSlotEntries,
} from "@/utils/CollectionSlots";
import { SectionTitle } from "./shared";
import styles from "./styles.module.css";

/**
 * Time-slot picker for a single day, sitting under the date picker in both detail panels. Slots the
 * site returned but that can't be booked (full, or no driver free) stay visible and disabled rather
 * than being hidden, so the coordinator can see the day is busy instead of looking empty.
 */
const CollectionSlotSection: FC<{
  slots: CollectionSlot[];
  value: number | null;
  onChange: (slotId: number) => void;
  loading: boolean;
  disabled?: boolean;
  /**
   * Whether a slot needs a free driver to be selectable. True when scheduling a new collection.
   * False when moving an existing one: the slot endpoint is container-keyed, so it can't exclude
   * *this* collection from the clash check and may report its own driver as busy — capacity is the
   * only constraint we can trust there.
   */
  requireAvailableDriver?: boolean;
}> = ({ slots, value, onChange, loading, disabled = false, requireAvailableDriver = true }) => {
  const { t } = useTranslation();
  const sorted = sortSlotEntries(slots);

  return (
    <div style={{ marginTop: 16 }}>
      <div className={styles.detailSectionHeader}>
        <SectionTitle>{t("containers:detail.timeSlot")}</SectionTitle>
        {!loading && sorted.length > 0 && (
          <Text span display="technical" size="xs" tt="uppercase" color="secondary">
            {t("containers:detail.slotCount", { count: sorted.length })}
          </Text>
        )}
      </div>

      {loading && (
        <div className={styles.sectionNote}>
          <Spinner loading inline />
        </div>
      )}

      {!loading && sorted.length === 0 && (
        <div className={styles.sectionNote}>
          <Text size="sm" color="tertiary">
            {t("containers:detail.noSlots")}
          </Text>
        </div>
      )}

      {!loading && sorted.length > 0 && (
        <div className={styles.chipRowWrap} style={{ marginTop: 12 }}>
          {sorted.map((entry) => {
            const past = isSlotPast(entry.slot);
            const selectable = isSlotSelectable(entry, { requireAvailableDriver }) && !disabled;
            const reason = past
              ? t("containers:detail.slotPast")
              : entry.slot.remainingCapacity <= 0
                ? t("containers:detail.slotFull")
                : !selectable && !disabled
                  ? t("containers:detail.slotNoDriver")
                  : t("containers:detail.slotRemaining", { count: entry.slot.remainingCapacity });

            return (
              <button
                key={entry.slot.id}
                type="button"
                disabled={!selectable}
                onClick={selectable ? () => onChange(entry.slot.id) : undefined}
                aria-pressed={entry.slot.id === value}
                title={`${formatSlotRange(entry.slot)} · ${reason}`}
                className={classNames(
                  styles.chip,
                  styles.chipStacked,
                  entry.slot.id === value ? styles.chipActive : "",
                  selectable ? "" : styles.chipDisabled
                )}
              >
                <span>{formatSlotTime(entry.slot.startTime)}</span>
                <span className={styles.chipSub}>{reason}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CollectionSlotSection;
